import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { User, Mail, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface CandidateInfoStepProps {
  onSubmit: (name: string, email: string) => void;
  onBack: () => void;
}

const CandidateInfoStep: React.FC<CandidateInfoStepProps> = ({ onSubmit, onBack }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');

  const validateForm = () => {
    let isValid = true;

    // Validate name
    if (!name.trim()) {
      setNameError('Name is required');
      isValid = false;
    } else {
      setNameError('');
    }

    // Validate email
    if (!email.trim()) {
      setEmailError('Email is required');
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email address');
      isValid = false;
    } else {
      setEmailError('');
    }

    return isValid;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(name, email);
    }
  };

  return (
    <div className="bg-white/90 rounded-lg border border-[#9b87f5]/30 shadow-lg w-full max-w-3xl px-8 py-10 backdrop-blur-sm">
      <h1 className="text-3xl font-bold text-center mb-3 text-[#1A1F2C]">Your Information</h1>
      
      <p className="text-center text-[#1A1F2C]/80 mb-8">
        Please provide your name and email before starting the interview
      </p>
      
      <div className="space-y-6 max-w-lg mx-auto">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-[#1A1F2C]">Your Name</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#1A1F2C]/50" size={18} />
            <Input
              id="name"
              className="bg-white border-[#9b87f5]/30 text-[#1A1F2C] pl-10 py-6 placeholder:text-[#1A1F2C]/50 focus-visible:ring-[#9b87f5]/50"
              placeholder="Enter your full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          {nameError && (
            <div className="flex items-center gap-1 text-red-500 text-sm mt-1">
              <AlertCircle size={14} />
              <span>{nameError}</span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-[#1A1F2C]">Your Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#1A1F2C]/50" size={18} />
            <Input
              id="email"
              type="email"
              className="bg-white border-[#9b87f5]/30 text-[#1A1F2C] pl-10 py-6 placeholder:text-[#1A1F2C]/50 focus-visible:ring-[#9b87f5]/50"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {emailError && (
            <div className="flex items-center gap-1 text-red-500 text-sm mt-1">
              <AlertCircle size={14} />
              <span>{emailError}</span>
            </div>
          )}
        </div>

        <div className="pt-6">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100 mb-6 flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle size={18} className="text-blue-500" />
            </div>
            <div>
              <p className="text-blue-700 text-sm">
                Your information will only be used to personalize your interview experience and to provide you with the interview recording afterwards.
              </p>
            </div>
          </div>
        </div>
        
        <div className="pt-2 flex justify-center">
          <Button 
            variant="purple"
            className="px-8 py-6 bg-gradient-to-r from-[#9b87f5] to-[#7662DA] hover:from-[#8A76E5] hover:to-[#6552CA] shadow-md text-white"
            onClick={handleSubmit}
          >
            Continue to Interview
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CandidateInfoStep; 